/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Branding } from "../../model/branding";
import { DeviceModel } from "../../model/device";
import { BrandingUiInfoKeys } from "../../model/entities";
import { Locale, LocaleKeys } from "../../model/locale";
import type { AlertOverlayAction, AlertOverlayConfig } from "../alertOverlay";
import { OWNER_SIGNIN_ERROR } from "./signIn.constants";
import { SIGN_IN_FALLBACK_ERROR_CODE } from "./common/authErrors";
import type { SignInErrorActionId } from "./common/signInErrorActions";
import {
  DEVICE_LIMIT_ERROR_CODES,
  resolveSignInErrorActions,
  SignInErrorAction
} from "./common/signInErrorActions";

/**
 * Builds the alert for a terminal sign-in error: the message, the code the
 * user can read to support, and the actions that code allows.
 *
 * Lives in the viewModel layer for the same reason `needHelpAlert` does — it
 * reads `Locale`, which the view layer must not touch directly. The policy it
 * applies is in `common/signInErrorActions`, which stays pure and testable;
 * this module only turns that policy into labelled buttons.
 */

/** Button label per action id. */
const ACTION_LABEL_KEYS: Record<SignInErrorActionId, string> = {
  [SignInErrorAction.Ok]: LocaleKeys.ALERT_ACTION_OK,
  [SignInErrorAction.Retry]: LocaleKeys.ACTION_RETRY,
  [SignInErrorAction.SignInAgain]: LocaleKeys.SIGN_IN_ACTION_SIGN_IN_AGAIN,
  [SignInErrorAction.NeedHelp]: LocaleKeys.NEED_HELP_TEXT,
  [SignInErrorAction.Exit]: LocaleKeys.ACTION_EXIT
};

/**
 * The body text with the error code in parentheses:
 * "invalid username or password (A431)".
 *
 * The code is rendered, not merely logged: it is the string a customer reads
 * off the screen to a support agent. **Our own catalog code wins.** It is the
 * number the client controls, the one the error-code catalog documents and the
 * one telemetry reports, so what the customer reads matches what an operator
 * searching the catalog or a dashboard can look up. A service's own body code
 * is a different vocabulary — versioned by the service, not by us, and absent
 * from the catalog entirely — so leading with it meant the string on screen and
 * the string in the diagnostics for the same failure did not agree.
 *
 * `serviceCode` remains the fallback for exactly one case: the catalog code is
 * the {@link SIGN_IN_FALLBACK_ERROR_CODE} catch-all, which every endpoint with
 * no allocated family reports — `v1/idpJwtToken`, where a mistyped password
 * lands, is the common one. `A010` names no cause, so a service code like
 * "IAS.UNAUTHORIZED" is strictly more use to the agent on the phone than
 * repeating the catch-all. Where neither exists the message stands alone rather
 * than trailing an empty "()".
 */
export function buildSignInErrorMessage(
  code: string,
  message: string,
  serviceCode: string = ""
): string {
  const hasCatalogCode: boolean =
    code.length > 0 && code !== SIGN_IN_FALLBACK_ERROR_CODE;
  // The catch-all falls back to the service code, then back to itself, so
  // "A010" still shows when the failure carried no envelope.
  const fallbackCode: string = serviceCode.length > 0 ? serviceCode : code;
  const shownCode: string = hasCatalogCode ? code : fallbackCode;
  if (shownCode.length === 0) {
    return message;
  }
  return message + " (" + shownCode + ")";
}

/**
 * Whether this device can close the app on its own. Exit is rendered on TV and
 * VegaOS only — a browser tab cannot close itself the same way (UAW-2577).
 *
 * VegaOS is checked by its injected platform flag rather than left to the
 * user-agent device-type sniff: the app runs there inside a ReactNativeWebView
 * whose UA need not carry a TV marker.
 */
function canExitApplication(): boolean {
  return DeviceModel.isTvDevice() || DeviceModel.isVegOsPlatform();
}

/** Labelled buttons for `ids`, with Exit dropped where the app cannot close. */
function buildActions(
  ids: ReadonlyArray<SignInErrorActionId>
): AlertOverlayAction[] {
  return ids
    .filter(
      (id: SignInErrorActionId): boolean =>
        id !== SignInErrorAction.Exit || canExitApplication()
    )
    .map(
      (id: SignInErrorActionId): AlertOverlayAction => ({
        id: id,
        label: Locale.getString(ACTION_LABEL_KEYS[id])
      })
    );
}

/**
 * Buttons for an auth-config failure. Retry leads — it re-fetches the
 * configuration and always survives the Exit filter, so a browser still has a
 * way forward. Sign In Again is never offered: with no configuration there is
 * no sign-in form to go back to.
 */
const AUTH_CONFIG_ERROR_ACTIONS: ReadonlyArray<SignInErrorActionId> = [
  SignInErrorAction.Retry,
  SignInErrorAction.NeedHelp,
  SignInErrorAction.Exit
];

/** A branded customer-support field, or "" when branding has none. */
function readSupportField(key: string): string {
  const value: unknown = Branding.getFallbackBrandingUiInfoForKey(key);
  return typeof value === "string" ? value : "";
}

/**
 * The catalog's TOO_MANY_DEVICES body, naming the operator's customer service
 * and phone number. Falls back to the neutral body ("Contact your operator…")
 * when branding lacks either, rather than rendering a blank or "undefined".
 */
function buildTooManyDevicesMessage(fallbackMessage: string): string {
  const supportName: string = readSupportField(
    BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME
  );
  const phoneNumber: string = readSupportField(
    BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER
  );
  const message: string =
    supportName.length > 0 && phoneNumber.length > 0
      ? Locale.getString(LocaleKeys.SIGN_IN_TOO_MANY_DEVICES_MESSAGE)
          .replace("{{customerSupportName}}", supportName)
          .replace("{{phoneNumber}}", phoneNumber)
      : Locale.getString(LocaleKeys.SIGN_IN_TOO_MANY_DEVICES_MESSAGE_NEUTRAL);
  return message.length > 0 ? message : fallbackMessage;
}

/**
 * The alert's title and body for `code`.
 *
 * Most codes use the generic "Sign In Error" title with whatever message the
 * flow rejected with. A463 (too many devices) uses the catalog's own copy
 * instead: the service's text does not say what the user has to do, and the
 * catalog's does — deactivate a device through customer service.
 */
function resolveAlertCopy(
  code: string,
  message: string
): { title: string; message: string } {
  if (DEVICE_LIMIT_ERROR_CODES.indexOf(code) !== -1) {
    const title: string = Locale.getString(
      LocaleKeys.SIGN_IN_TOO_MANY_DEVICES_TITLE
    );
    return {
      title:
        title.length > 0
          ? title
          : Locale.getString(LocaleKeys.SIGN_IN_ERROR_TITLE),
      message: buildTooManyDevicesMessage(message)
    };
  }
  return {
    title: Locale.getString(LocaleKeys.SIGN_IN_ERROR_TITLE),
    message: message
  };
}

/**
 * The full alert configuration for a sign-in error.
 *
 * Always carries at least one action — `resolveSignInErrorActions` is total and
 * never returns Exit alone — so this can never produce the actionless overlay
 * that leaves a device with no way out. That matters more than it looks:
 * `AlertOverlayController` ignores BACK when the action list is empty.
 *
 * Tagged with {@link OWNER_SIGNIN_ERROR}. The owner is what marks this overlay
 * as speaking for a sign-in failure, and `NavigationInterceptor` holds any
 * unrelated screen transition for as long as one is on screen — the user must
 * be able to read the code and pick an action without the screen underneath
 * changing out from under it (UAW-2415).
 */
export function buildSignInErrorAlert(
  code: string,
  message: string,
  serviceCode: string = ""
): AlertOverlayConfig {
  const actions: AlertOverlayAction[] = buildActions(
    resolveSignInErrorActions(code)
  );

  const copy: { title: string; message: string } = resolveAlertCopy(
    code,
    message
  );

  return {
    title: copy.title,
    message: buildSignInErrorMessage(code, copy.message, serviceCode),
    actions: actions,
    owner: OWNER_SIGNIN_ERROR
  };
}

/**
 * The alert for a failed `GET /v3/authConfigurations` at sign-in start.
 *
 * Without the configuration the sign-in screen has no tabs and no form, so
 * this alert is the only thing on it — it must always carry a way forward
 * ({@link AUTH_CONFIG_ERROR_ACTIONS}).
 *
 * The copy is the catalog's for the whole A1xx family: every entry shares one
 * title and body (`PROBLEM_DVR_SERVICE_TITLE` / `PROBLEM_CONNECTING_SERVICE_BODY`
 * in docs/unity-client-error-codes), so the code in parentheses is what tells
 * the causes apart — A100 bad request, A101 not configured, A102 server error,
 * A105 timeout, A108/A109 connection, A112/A114/A116 unusable response, A113
 * anything else. The service's own message is not shown: it is not localised
 * and names nothing the subscriber can act on.
 */
export function buildAuthConfigErrorAlert(
  code: string,
  serviceCode: string = ""
): AlertOverlayConfig {
  return {
    title: Locale.getString(
      LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_TITLE
    ),
    message: buildSignInErrorMessage(
      code,
      Locale.getString(
        LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
      ),
      serviceCode
    ),
    actions: buildActions(AUTH_CONFIG_ERROR_ACTIONS),
    owner: OWNER_SIGNIN_ERROR
  };
}
